export interface ApiErrorBody {
  error: {
    code: string;
    fieldErrors?: Array<{ field: string; message: string }>;
    message: string;
    requestId: string;
    retryAfterSeconds?: number;
    status?: number;
    mutationDisposition?: "NOT_APPLIED";
  };
}

export class CyraError extends Error {
  readonly code: string;
  readonly requestId: string;
  readonly fieldErrors: ReadonlyArray<{ field: string; message: string }>;
  readonly retryAfterSeconds: number | undefined;
  readonly status: number | undefined;
  readonly mutationDisposition: "NOT_APPLIED" | undefined;

  constructor(body: ApiErrorBody["error"]) {
    super(body.message);
    this.name = "CyraError";
    this.code = body.code;
    this.requestId = body.requestId;
    this.fieldErrors = body.fieldErrors ?? [];
    this.retryAfterSeconds = body.retryAfterSeconds;
    this.status = body.status;
    this.mutationDisposition = body.mutationDisposition;
  }
}

export function errorMessage(error: unknown): string {
  const code = firebaseErrorCode(error);
  if (code) return firebaseMessage(code);
  return error instanceof Error ? error.message : "The request could not be completed.";
}

function firebaseErrorCode(error: unknown): string | null {
  if (!error || typeof error !== "object" || !("code" in error)) return null;
  const code = error.code;
  return typeof code === "string" && code.startsWith("auth/") ? code : null;
}

function firebaseMessage(code: string): string {
  switch (code) {
    case "auth/invalid-verification-code":
      return "The authenticator code is incorrect or expired.";
    case "auth/code-expired":
    case "auth/multi-factor-info-not-found":
    case "auth/multi-factor-auth-required":
      return "The MFA challenge expired. Restart sign-in.";
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
    case "auth/user-not-found":
    case "auth/wrong-password":
      return "The email or password does not match.";
    case "auth/too-many-requests":
      return "Too many attempts. Wait before trying again.";
    case "auth/network-request-failed":
      return "The authentication service is unavailable. Try again.";
    case "auth/user-disabled":
      return "This account is unavailable.";
    default:
      return "Authentication could not be completed.";
  }
}
